import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, Lock, Smartphone, AlertCircle } from 'lucide-react';
import { checkAdminAuthSession, saveAdminAuthSession } from '@/utils/deviceFingerprint';
import { useToast } from '@/hooks/use-toast';
import { parseInvokeError, formatRateLimitMessage } from '@/utils/parseFunctionInvokeError';

type AuthStage = 'password' | 'totp' | 'complete';

interface AdminAuthGateProps {
  onAuthComplete: () => void;
}

export function AdminAuthGate({ onAuthComplete }: AdminAuthGateProps) {
  const [stage, setStage] = useState<AuthStage>('password');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const { toast } = useToast();

  // Check existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const hasValidSession = await checkAdminAuthSession();
      if (hasValidSession) {
        onAuthComplete();
      } else {
        setIsCheckingSession(false);
      }
    };
    
    checkSession();
  }, [onAuthComplete]);

  const validatePassword = async () => {
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const invokeResult = await supabase.functions.invoke('validate-admin-auth', {
        body: { password, stage: 'password' }
      });

      const { data, error: invokeError } = invokeResult;
      const rawResponse = (invokeResult as unknown as { response?: Response }).response;

      if (invokeError) {
        const parsed = await parseInvokeError(invokeError, rawResponse);
        console.error('[AdminAuthGate] Password validation error:', parsed);
        
        if (parsed.status === 429 || parsed.message.includes('Too many')) {
          setError(`Too many attempts. ${formatRateLimitMessage(parsed.blockedUntil)}`);
        } else {
          setError(parsed.message || 'Failed to validate password. Please try again.');
        }
        return;
      }

      if (!data?.valid) {
        setError('Invalid password. Please try again.');
        setPassword('');
      } else {
        setStage('totp');
        setError('');
      }
    } catch (err) {
      console.error('[AdminAuthGate] Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const ensureSupabaseSession = async () => {
    try {
      // Check if session exists
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        console.log('[AdminAuthGate] Supabase session already active');
        return;
      }
      
      // Known admin email
      const ADMIN_EMAIL = 'justus.wiedemann@celsios.io';
      
      console.log('[AdminAuthGate] Creating Supabase session for admin...');
      
      // Call login-existing-user to create session
      const { data, error } = await supabase.functions.invoke('login-existing-user', {
        body: { email: ADMIN_EMAIL }
      });
      
      if (error || !data?.session) {
        console.error('[AdminAuthGate] Failed to create Supabase session:', error);
        toast({
          title: 'Warning',
          description: 'Admin gate passed, but Supabase session could not be created.',
          variant: 'destructive',
        });
        return;
      }
      
      // Set session in client
      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token
      });
      
      console.log('[AdminAuthGate] ✅ Supabase session created successfully');
      
    } catch (err) {
      console.error('[AdminAuthGate] Error ensuring Supabase session:', err);
      // Don't block admin access, just log the error
    }
  };

  const validateTOTP = async (code: string) => {
    setIsLoading(true);
    setError('');

    try {
      const invokeResult = await supabase.functions.invoke('validate-admin-auth', {
        body: { totpCode: code, stage: 'totp' }
      });

      const { data, error: invokeError } = invokeResult;
      const rawResponse = (invokeResult as unknown as { response?: Response }).response;

      if (invokeError) {
        const parsed = await parseInvokeError(invokeError, rawResponse);
        console.error('[AdminAuthGate] TOTP validation error:', parsed);
        
        if (parsed.status === 429 || parsed.message.includes('Too many')) {
          setError(`Too many attempts. ${formatRateLimitMessage(parsed.blockedUntil)}`);
        } else {
          setError(parsed.message || 'Failed to validate code. Please try again.');
        }
        setTotpCode('');
        return;
      }

      if (!data?.valid) {
        setError('Invalid authentication code. Please try again.');
        setTotpCode('');
      } else {
        await saveAdminAuthSession();
        
        // Auto-create Supabase session
        await ensureSupabaseSession();
        
        toast({
          title: 'Authentication Successful',
          description: 'This device will be remembered for 30 days.',
        });
        setStage('complete');
        onAuthComplete();
      }
    } catch (err) {
      console.error('[AdminAuthGate] Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
      setTotpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTOTPComplete = (value: string) => {
    if (value.length === 6) {
      validateTOTP(value);
    }
  };

  const handleBackToPassword = () => {
    setStage('password');
    setTotpCode('');
    setError('');
  };

  // Loading screen while checking session
  if (isCheckingSession) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Password Stage
  if (stage === 'password') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Lock className="h-5 w-5 text-primary" />
            <CardTitle>Security Check</CardTitle>
          </div>
          <CardDescription>
            Enter the master password to continue
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="password">Master Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter master password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && validatePassword()}
              disabled={isLoading}
              autoFocus
            />
          </div>

          <Button
            onClick={validatePassword}
            disabled={!password.trim() || isLoading}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Validating...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // TOTP Stage
  if (stage === 'totp') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <CardTitle>Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>
            Enter your 6-digit authentication code
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col items-center space-y-4">
            <InputOTP
              maxLength={6}
              value={totpCode}
              onChange={setTotpCode}
              onComplete={handleTOTPComplete}
              disabled={isLoading}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>

            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Verifying code...
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleBackToPassword}
              disabled={isLoading}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={() => validateTOTP(totpCode)}
              disabled={totpCode.length !== 6 || isLoading}
              className="flex-1"
            >
              Verify
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}
